import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { Button } from "../../components/ui/Button";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { motion } from "framer-motion";

interface ReportConfirmationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

// Modal Wrapper Component
const ModalWrapper = ({
    isOpen,
    onClose,
    onConfirm,
}: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) => (
    <div
        className="fixed inset-0 overflow-y-auto overflow-x-hidden flex z-[99999]"
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            zIndex: 99999,
            padding: "1rem",
        }}>
        {/* Backdrop */}
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(4px)",
            }}
            onClick={onClose}
        />

        {/* Modal Content */}
        <div
            className="bg-white rounded-xl p-5 shadow-2xl max-w-sm w-full relative z-[100000] mx-4 my-auto"
            style={{
                position: "relative",
                zIndex: 100000,
                maxWidth: "24rem",
                width: "calc(100% - 2rem)",
                backgroundColor: "white",
                borderRadius: "0.75rem",
                padding: "1.25rem",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                marginTop: "calc(20vh)",
            }}
            onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        Generate Report?
                    </h2>
                    <p className="text-base text-gray-600">
                        Confirm to generate your assessment report
                    </p>
                </motion.div>
            </div>

            <div className="flex flex-col items-center space-y-6">
                <motion.div
                    className="w-32 h-32"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}>
                    <DotLottieReact
                        src="https://lottie.host/46e450df-6678-4da5-be34-1d9aae84569e/CYkf8XRMqg.lottie"
                        speed={0.5}
                        autoplay
                    />
                </motion.div>

                <motion.div
                    className="text-center space-y-4"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-sm text-gray-700">
                            Please note that if you haven't completed all
                            interview questions, this may impact your final
                            assessment score and report accuracy.
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    className="flex gap-3 w-full justify-center pt-4"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="min-w-[120px] h-10 text-sm hover:bg-gray-100 transition-colors"
                        style={{
                            color: "var(--color-primary)",
                            borderColor: "var(--color-primary)",
                        }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        className="min-w-[120px] h-10 text-sm"
                        style={{
                            backgroundColor: "var(--color-primary)",
                            color: "white",
                        }}>
                        Generate Report
                    </Button>
                </motion.div>
            </div>
        </div>
    </div>
);

const ReportConfirmationDialog = React.memo(function ReportConfirmationDialog({
    isOpen,
    onClose,
    onConfirm,
}: ReportConfirmationDialogProps) {
    // Scroll to top when dialog opens
    useEffect(() => {
        if (isOpen) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [isOpen]);

    // If dialog is not open, don't render anything
    if (!isOpen) return null;

    // Create a portal to render the modal at the root level
    return (
        <>
            {typeof document !== "undefined" &&
                ReactDOM.createPortal(
                    <ModalWrapper
                        isOpen={isOpen}
                        onClose={onClose}
                        onConfirm={onConfirm}
                    />,
                    document.body
                )}
        </>
    );
});

export default ReportConfirmationDialog;
